export function LicensingProcess() {
  const timeline = [
    {
      step: '1',
      title: 'Jurisdiction Verification',
      duration: '1-2 days',
      description: 'Confirm whether your property is in Township of Langley or City of Langley and verify zoning eligibility for short-term rentals.'
    },
    {
      step: '2',
      title: 'Document Preparation',
      duration: '3-5 days',
      description: 'Gather required documentation including property title, insurance, floor plans, and safety compliance certificates.'
    },
    {
      step: '3',
      title: 'Business License Application',
      duration: '2-3 weeks',
      description: 'Submit business license application to appropriate municipality with all supporting documents and applicable fees.'
    },
    {
      step: '4',
      title: 'Safety Inspection',
      duration: '1-2 weeks',
      description: 'Schedule and complete property safety inspection if required by jurisdiction, addressing any deficiencies identified.'
    },
    {
      step: '5',
      title: 'License Approval & Activation',
      duration: '1 week',
      description: 'Receive business license approval, complete final registration steps, and activate your short-term rental listing.'
    }
  ];

  const requiredDocs = [
    'Proof of property ownership or authorization',
    'Property insurance with STR coverage',
    'Floor plan showing sleeping areas',
    'Smoke and CO detector certification',
    'Fire extinguisher documentation',
    'Emergency contact information',
    'Parking plan and availability',
    'Waste management plan',
    'Strata approval (if applicable)',
    'Landlord permission (if renting)'
  ];

  const fees = [
    { item: 'Business License (Township)', cost: '$150-$250/year' },
    { item: 'Business License (City)', cost: '$200-$300/year' },
    { item: 'Safety Inspection (if required)', cost: '$150-$300' },
    { item: 'Fire Safety Equipment', cost: '$100-$200' },
    { item: 'Insurance Premium Increase', cost: '$300-$600/year' },
    { item: 'Professional Consultation', cost: '$500-$1,000' }
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-[#505A5A] mb-6">
            Licensing Process & Requirements
          </h2>
          <p className="text-lg text-[#505A5A]/70 leading-relaxed">
            Navigate Langley's licensing process with confidence. Our step-by-step guidance ensures smooth approval and full compliance.
          </p>
        </div>

        {/* Timeline */}
        <div className="mb-16">
          <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A] mb-8 text-center">
            Complete Timeline: 5-8 Weeks
          </h3>
          <div className="space-y-6">
            {timeline.map((item, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 lg:p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="flex flex-col lg:flex-row items-start gap-6">
                  <div className="w-16 h-16 bg-[#882629] rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-2xl font-bold">{item.step}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-3">
                      <h4 className="font-['Fraunces'] text-xl font-bold text-[#505A5A]">
                        {item.title}
                      </h4>
                      <span className="text-sm font-semibold text-[#882629] mt-2 lg:mt-0">
                        {item.duration}
                      </span>
                    </div>
                    <p className="text-[#505A5A]/70 leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Required Documents */}
        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#882629] rounded-xl flex items-center justify-center">
                <i className="ri-file-list-3-line text-white text-2xl"></i>
              </div>
              <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A]">
                Required Documentation
              </h3>
            </div>
            <ul className="space-y-3">
              {requiredDocs.map((doc, index) => (
                <li key={index} className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-[#882629] text-lg flex-shrink-0 w-5 h-5 flex items-center justify-center mt-1"></i>
                  <span className="text-[#505A5A]/80">{doc}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Fee Breakdown */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#882629] rounded-xl flex items-center justify-center">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h3 className="font-['Fraunces'] text-2xl font-bold text-[#505A5A]">
                Cost Breakdown
              </h3>
            </div>
            <div className="space-y-4">
              {fees.map((fee, index) => (
                <div key={index} className="flex items-center justify-between pb-3 border-b border-[#505A5A]/10 last:border-0">
                  <span className="text-[#505A5A]/80">{fee.item}</span>
                  <span className="font-semibold text-[#505A5A]">{fee.cost}</span>
                </div>
              ))}
              <div className="pt-4 mt-4 border-t-2 border-[#882629]">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#505A5A] text-lg">First Year Total</span>
                  <span className="font-bold text-[#882629] text-xl">$1,400-$2,650</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Help CTA */}
        <div className="bg-[#882629] rounded-2xl p-8 lg:p-12 text-center text-white">
          <h3 className="font-['Fraunces'] text-3xl font-bold mb-4">
            We Handle the Entire Process
          </h3>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            From jurisdiction verification to license approval, our team manages every step of your Langley short-term rental licensing. Focus on your property while we handle the paperwork.
          </p>
          <button 
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
              }
            }}
            className="bg-white text-[#882629] px-10 py-5 rounded-full text-lg font-medium hover:bg-[#F0F0F0] transition-all cursor-pointer whitespace-nowrap shadow-xl"
          >
            Start Your Application
          </button>
        </div>
      </div>
    </section>
  );
}
